import React, { useContext } from 'react'
import { Dialog, Box, IconButton } from '@mui/material'
import { withStyles } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'

import { useWidth } from '../../../core/utils'
import { ModalContext } from '../contexts'
import { TYPES } from '../constants'
import { modalStyles } from './modal.styles'

/**
 * @typedef {object} ModalComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 */

/**
 * Un componente de modal que consume el contexto de Modal para renderizar diálogos.
 * 
 * Este componente lee su configuración del `ModalContext` y renderiza un `Dialog` de Material-UI
 * con el componente especificado. Soporta diferentes tipos de modal (normal, pantalla completa, responsivo)
 * y puede incluir un botón de cierre opcional.
 * 
 * **Nota:** Este componente debe estar envuelto en un `ModalProvider` para funcionar correctamente.
 * 
 * @component
 * @example
 * // Este componente se usa internamente y consume el contexto.
 * // Para abrir un modal, usa el hook useModal:
 * 
 * const MyPage = () => {
 *   const { open } = useModal();
 * 
 *   const handleOpenModal = () => {
 *     open({
 *       Component: MyModalContent,
 *       data: { userId: 123 },
 *       type: 'responsive',
 *       closable: true,
 *     });
 *   };
 * 
 *   return <button onClick={handleOpenModal}>Abrir Modal</button>;
 * };
 * 
 * @param {ModalComponentProps} props - Las props del componente ModalComponent.
 */
export const ModalComponent = withStyles(modalStyles)(({ classes }) => {
  const {
    actions: { close },
    isOpen,
    type,
    Component,
    data,
    maxWidth,
    closable = false,
  } = useContext(ModalContext)

  const isMobileWidth = useWidth() === 'xs'

  return (
    <Dialog
      open={isOpen}
      fullScreen={type === TYPES.FULL_SCREEN || (type === TYPES.RESPONSIVE && isMobileWidth)}
      className={classes[type]}
      maxWidth={maxWidth}
      fullWidth
    >
      {closable ? (
        <Box display="flex" justifyContent="flex-end">
          <IconButton onClick={close} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      ) : null}
      {Component ? <Component close={close} {...data} /> : null}
    </Dialog>
  )
})
